import { createUuid, CustomError } from '@core/utils';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import ky from 'ky';

import type { UnauthApi } from '@/api';
import { AuthQueryOptions } from '@/api';
import type { GEOLOCATION_VIEW_TYPE } from '@/constants/geo';
import { useAuthStore } from '@/stores/AuthStore';
import { useGlobalStore } from '@/stores/GlobalStore';

import { useGeoApis } from './useGeoApis';

export const DEFAULT_LOCATION = {
  isDefaultLocation: true,
  isLocationServiceConsent: false,
  hasLocationPermission: false,
  longitude: 126.95629,
  latitude: 37.47793,
  region: '서울특별시',
  codeRegion: 'C01-01',
  subRegion: '관악구',
  codeSubRegion: 'C01-01-21',
};

const LOCATION_DRAWER_SESSION_KEY = 'denied-location-drawer';
const LOCATION_QUERY_KEY = createUuid();

type GeolocationResponse = Awaited<
  ReturnType<
    (typeof UnauthApi)['get:/wello/v2/allows/wello-policy-code/reverse-geocoding']
  >
>['data'];

interface UseLocationOptions {
  /** 초기 데이터 가져오기 활성화 여부 */
  enabledInitialFetch?: boolean;
  geolocationViewType?: GEOLOCATION_VIEW_TYPE;
}

export const useLocation = ({
  enabledInitialFetch = false,
  geolocationViewType,
}: UseLocationOptions = {}) => {
  const { getGeocoding, getGeolocation } = useGeoApis();
  const isLogin = useAuthStore((state) => state.isLogin);

  const locationServiceConsent = useQuery({
    ...AuthQueryOptions[
      'get:/wello/v2/auth/member-wello/location-service-consent'
    ](),
    enabled: !!isLogin,
    select: (data) =>
      isLogin ? (data.context?.locationServiceConsentYN ?? false) : true,
  });

  const locationDrawerCallback = useGlobalStore(
    ({ setHandleLocationServiceConsentDrawer }) =>
      setHandleLocationServiceConsentDrawer,
  );

  return useQuery({
    queryKey: [LOCATION_QUERY_KEY, locationServiceConsent.data],
    placeholderData: keepPreviousData,
    staleTime: 0,
    gcTime: 0,
    queryFn: async () => {
      const isLocationServiceConsent = isLogin
        ? locationServiceConsent.data
        : true;

      if (isLocationServiceConsent === undefined)
        throw new CustomError({
          return_message: '위치 기반 서비스 동의 정보가 없습니다.',
        });

      if (!isLocationServiceConsent) {
        let isDenied =
          sessionStorage.getItem(LOCATION_DRAWER_SESSION_KEY) === 'true';

        if (isDenied) return DEFAULT_LOCATION;

        isDenied = await new Promise((resolve) => {
          locationDrawerCallback(async (isAgree) => {
            resolve(!isAgree);
          });
        });

        if (isDenied) {
          sessionStorage.setItem(LOCATION_DRAWER_SESSION_KEY, 'true');

          return DEFAULT_LOCATION;
        }
      }

      let hasLocationPermission = true;

      const res = await new Promise<
        NonNullable<GeolocationResponse>['context']
      >((resolve) => {
        navigator.geolocation.getCurrentPosition(
          async ({ coords }) => {
            try {
              const { data } = await getGeocoding({
                coords: `${coords.longitude},${coords.latitude}`,
                geolocationViewType,
              });

              resolve({
                ...data?.context,
                latitude: String(coords.latitude),
                longitude: String(coords.longitude),
              });
            } catch {
              // 해외 좌표 등 지역 정보 확인 불가 시 null 반환
              resolve(undefined);
            }
          },

          async (error) => {
            if (error.code === error.PERMISSION_DENIED) {
              hasLocationPermission = false;
            }

            try {
              const ip = await ky.get<string>('https://api.ip.pe.kr').text();

              const { data } = await getGeolocation({
                ip,
                geolocationViewType,
              });

              resolve(data?.context);
            } catch {
              // 해외 접속 등 지역 정보 확인 불가 시 null 반환
              resolve(undefined);
            }
          },
          {
            enableHighAccuracy: true,
            timeout: 5_000,
            maximumAge: 0,
          },
        );
      });

      const {
        region,
        codeRegion: code_region,
        subRegion: sub_region,
        codeSubRegion: code_sub_region,
      } = res ?? {};

      // 해외 접속 등 지역 정보 확인이 어려운 경우 기본 지역으로 설정
      if (!res || !region || !code_region || !sub_region || !code_sub_region)
        return DEFAULT_LOCATION;

      return {
        isDefaultLocation: false,
        isLocationServiceConsent,
        hasLocationPermission,
        longitude: res.longitude ? Number(res.longitude) : null,
        latitude: res.latitude ? Number(res.latitude) : null,
        region,
        codeRegion: code_region,
        subRegion: sub_region,
        codeSubRegion: code_sub_region,
      };
    },
    enabled:
      isLogin !== undefined &&
      (!isLogin || locationServiceConsent.data !== undefined) &&
      enabledInitialFetch,
  });
};

export type LocationData = Exclude<
  Awaited<ReturnType<typeof useLocation>>['data'],
  undefined
>;
